/*

? The cards a chat message is drawn as (19 Oct 2026, the chat cards redesign, the app's look): the
? delivery glyph, a person or business avatar, a group of shared listings, a shared location, a
? business's contact card and a requirement response.

  Every card draws only what the server serves (lib/chat/messageCards.ts, lib/chat/chatPropertyCard.ts):
    - `attached_listings_details` for listings (a listing the viewer may not read comes back without a
      title and reads "Listing unavailable");
    - `location_details` `{ title, address, latitude, longitude, maps_url }` on a location message;
    - `contact_details` `{ title, profile_type, logo_url, phone, email }` on a contact card;
    - `requirement_details` `{ title, transaction_type, property_category, property_type }` on a
      requirement response.
  Clients never parse a message's body for any of it.

*/

/* Common Imports */

import { styled, useTheme } from "@mui/system";
import { NextRouter } from "next/router";
import React from "react";

/* Component Imports */

import { Checkbox, Typography } from "@mui/material";
import BeegruButton from "../../../common-components/buttons/BeegruButton";
import { ChatMapPreview } from "./ChatMaps";

/* Icon Imports */

import {
	AccessTime,
	ApartmentOutlined,
	ChevronRight,
	Done,
	DoneAll,
	HomeOutlined,
	HomeWorkOutlined,
	KeyboardArrowDown,
	LocationOnOutlined,
	MailOutline,
	PhoneOutlined,
	ReplyOutlined,
} from "@mui/icons-material";

/* --- Shared helpers --- */

/** Two letters from the first two words of [name] ("Sentient Developers" -> "SD"), or "". */
export const initialsOf = (name: string): string =>
	String(name || "")
		.trim()
		.split(/\s+/)
		.filter((word: string) => word.length > 0)
		.slice(0, 2)
		.map((word: string) => word.charAt(0).toUpperCase())
		.join("");

/** The card's words in the site's three languages. */
const cardLang = (locale?: string) => {
	const ar: boolean = locale === "ar-AE";
	const kn: boolean = locale === "kn-IN";
	return {
		listings_shared: (count: number): string =>
			ar ? `تمت مشاركة ${count} عقارات` : kn ? `${count} ಪಟ್ಟಿಗಳನ್ನು ಹಂಚಲಾಗಿದೆ` : `${count} listings shared`,
		view_all: ar ? "عرض الكل" : kn ? "ಎಲ್ಲವನ್ನೂ ನೋಡಿ" : "View all",
		show_less: ar ? "عرض أقل" : kn ? "ಕಡಿಮೆ ತೋರಿಸಿ" : "Show less",
		unavailable: ar ? "القائمة غير متاحة" : kn ? "ಪಟ್ಟಿ ಲಭ್ಯವಿಲ್ಲ" : "Listing unavailable",
		call: ar ? "اتصال" : kn ? "ಕರೆ ಮಾಡಿ" : "Call",
		email: ar ? "بريد إلكتروني" : kn ? "ಇಮೇಲ್" : "Email",
		requirement_response: ar ? "رد على المتطلب" : kn ? "ಅವಶ್ಯಕತೆಗೆ ಪ್ರತಿಕ್ರಿಯೆ" : "Requirement Response",
		seeking: (type: string): string => {
			const kind: string = String(type || "").toLowerCase();
			if (kind === "rent") return ar ? "يبحث عن إيجار" : kn ? "ಬಾಡಿಗೆಗೆ ಹುಡುಕುತ್ತಿದ್ದಾರೆ" : "Seeking To Rent";
			if (kind === "buy" || kind === "sale") return ar ? "يبحث عن شراء" : kn ? "ಖರೀದಿಸಲು ಹುಡುಕುತ್ತಿದ್ದಾರೆ" : "Seeking To Buy";
			return ar ? "متطلب" : kn ? "ಅವಶ್ಯಕತೆ" : "Requirement";
		},
		more_attachments: (count: number): string =>
			ar ? `+${count} مرفقات أخرى` : kn ? `+${count} ಇನ್ನಷ್ಟು ಲಗತ್ತುಗಳು` : `+${count} More attachments`,
	};
};

/* --- The delivery glyph (16.png) --- */

export type ReceiptState = "sending" | "sent" | "delivered" | "read";

/**
 * The glyph beside an outgoing time: a clock while it is on its way, one grey tick sent, two grey
 * ticks delivered to everyone, two orange ticks read by everyone (lib/chat/messageState.ts).
 */
export const ReceiptGlyph = ({ state }: { state: ReceiptState }) => {
	const theme: any = useTheme();
	const sx = { fontSize: "1rem", marginLeft: "0.25rem", flexShrink: 0 };
	if (state === "sending") {
		return (
			<AccessTime
				aria-label="Sending"
				sx={{ ...sx, fontSize: "0.875rem", color: theme.palette.text.secondary }}
			/>
		);
	}
	if (state === "sent") {
		return (
			<Done
				aria-label="Sent"
				sx={{ ...sx, color: theme.palette.text.secondary }}
			/>
		);
	}
	return (
		<DoneAll
			aria-label={state === "read" ? "Read" : "Delivered"}
			sx={{ ...sx, color: state === "read" ? theme.palette.primary.dark : theme.palette.text.secondary }}
		/>
	);
};

/* --- Avatars --- */

const AvatarFrame = styled("div")(({ theme }) => ({
	position: "relative",
	flexShrink: 0,
	borderRadius: "30%",
	background: theme.palette.background.highlight,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	color: theme.palette.primary.dark,
	fontWeight: 600,
}));

const AvatarImage = styled("img")({
	width: "100%",
	height: "100%",
	objectFit: "cover",
	borderRadius: "30%",
});

/**
 * A person's or business's picture as a rounded square, [size] across. The initials of [name] show
 * when there is no picture or it fails to load. [badge] sits on the bottom-right corner.
 */
export const ChatAvatar = ({
	src,
	name,
	size,
	badge,
}: {
	src?: string;
	name: string;
	size: string;
	badge?: React.ReactNode;
}) => {
	const [failed, setFailed] = React.useState<boolean>(false);
	React.useEffect(() => setFailed(false), [src]);
	return (
		<AvatarFrame sx={{ width: size, height: size, fontSize: `calc(${size} * 0.36)` }}>
			{src && !failed ? (
				<AvatarImage
					src={src}
					alt={name}
					referrerPolicy="no-referrer"
					onError={() => setFailed(true)}
				/>
			) : (
				initialsOf(name)
			)}
			{badge}
		</AvatarFrame>
	);
};

/* --- Shared listings (14.png) --- */

type ListingCard = {
	property_id?: string;
	title?: string;
	location_title?: string;
	cover_image_url?: string;
	price_label?: string;
	bhk_label?: string;
	slug?: string;
};

/* How many rows a group shows before "View all" (shares are capped at 5). */
const LISTINGS_COLLAPSED: number = 3;

const GroupCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "18.5rem",
	maxWidth: "100%",
	borderRadius: "0.75rem",
	overflow: "hidden",
	border: `1px solid ${theme.palette.divider}`,
	background: theme.palette.background.paper,
}));

/* The wash behind a card's header strip (a listings group, a requirement), 19 Oct 2026. */
const CARD_HEADER_BG: string = "#f7eee7";

const GroupHeader = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.375rem",
	padding: "0.5rem 0.75rem",
	// The card headers' wash, the app's (19 Oct 2026): primary-100.
	background: theme.palette.mode === "dark" ? theme.palette.background.highlight : CARD_HEADER_BG,
	color: theme.palette.primary.dark,
}));

const RowLink = styled("a")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.75rem",
	padding: "0.625rem 0.75rem",
	color: theme.palette.text.primary,
	textDecoration: "none",
	"& + &": {
		borderTop: `1px solid ${theme.palette.divider}`,
	},
}));

const Thumb = styled("img")({
	width: "3.5rem",
	height: "3.5rem",
	borderRadius: "0.5rem",
	objectFit: "cover",
	flexShrink: 0,
});

const ThumbPlaceholder = styled("div")(({ theme }) => ({
	width: "3.5rem",
	height: "3.5rem",
	borderRadius: "0.5rem",
	flexShrink: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	background: theme.palette.background.highlight,
	color: theme.palette.primary.dark,
}));

const RowText = styled("div")({
	display: "flex",
	flexDirection: "column",
	gap: "0.125rem",
	minWidth: 0,
	flex: 1,
});

const OneLine = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

/* A text button inside a card ("View all", "+2 More attachments"). */
const CardTextButton = styled("button")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.25rem",
	border: "none",
	background: "transparent",
	padding: "0.5rem 0.75rem",
	cursor: "pointer",
	color: theme.palette.primary.dark,
	fontWeight: 600,
	fontSize: "0.8125rem",
	fontFamily: "inherit",
}));

/** One listing row: thumbnail, locality, title, price and BHK, opening the listing in a new tab. */
const ListingRow = ({ router, listing }: { router: NextRouter; listing: ListingCard }) => {
	const lang = cardLang(router.locale);
	const available: boolean = !!listing.title;
	const href: string | undefined = available && listing.slug ? `/${router.locale || "en-IN"}/properties/${listing.slug}` : undefined;
	const meta: string = [listing.price_label, listing.bhk_label].filter((part) => part && String(part).trim() !== "").join(" · ");
	return (
		<RowLink
			href={href}
			target={href ? "_blank" : undefined}
			rel={href ? "noopener noreferrer" : undefined}
			sx={{ cursor: href ? "pointer" : "default" }}
		>
			{available && listing.cover_image_url ? (
				<Thumb
					src={listing.cover_image_url}
					alt={listing.title}
					referrerPolicy="no-referrer"
				/>
			) : (
				<ThumbPlaceholder>
					<HomeWorkOutlined sx={{ fontSize: "1.5rem" }} />
				</ThumbPlaceholder>
			)}
			<RowText>
				{available && listing.location_title ? (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "flex", alignItems: "center", gap: "0.25rem", ...OneLine }}
					>
						<LocationOnOutlined sx={{ fontSize: "0.875rem" }} />
						<span style={OneLine}>{listing.location_title}</span>
					</Typography>
				) : null}
				<Typography
					variant="body2"
					sx={{ fontWeight: 600, ...OneLine }}
				>
					{available ? listing.title : lang.unavailable}
				</Typography>
				{available && meta ? (
					<Typography
						variant="caption"
						sx={{ color: "primary.dark", fontWeight: 500, ...OneLine }}
					>
						{meta}
					</Typography>
				) : null}
			</RowText>
			{href ? <ChevronRight sx={{ fontSize: "1.25rem", color: "text.secondary", flexShrink: 0 }} /> : null}
		</RowLink>
	);
};

/**
 * Two or more listings as one grouped card: "N listings shared", three rows, then "View all" to show
 * the rest (a share holds at most five).
 */
export const SharedListingsCard = ({
	router,
	listings,
	actions,
}: {
	router: NextRouter;
	listings: Array<ListingCard>;
	/* "I'm Interested" / "Not interested" on the other side's share, while unanswered (gap A7). */
	actions?: React.ReactNode;
}) => {
	const lang = cardLang(router.locale);
	const [expanded, setExpanded] = React.useState<boolean>(false);
	const shown: Array<ListingCard> = (listings || []).filter((listing) => !!listing);
	if (shown.length === 0) return null;
	const visible: Array<ListingCard> = expanded ? shown : shown.slice(0, LISTINGS_COLLAPSED);
	return (
		<GroupCard>
			<GroupHeader>
				<ApartmentOutlined sx={{ fontSize: "1rem" }} />
				<Typography
					variant="caption"
					sx={{ fontWeight: 600, fontSize: "0.8125rem" }}
				>
					{lang.listings_shared(shown.length)}
				</Typography>
			</GroupHeader>
			{visible.map((listing: ListingCard, index: number) => (
				<ListingRow
					key={String(listing.property_id || "") || index}
					router={router}
					listing={listing}
				/>
			))}
			{shown.length > LISTINGS_COLLAPSED ? (
				<CardTextButton
					type="button"
					onClick={() => setExpanded(!expanded)}
					sx={{ borderTop: (theme: any) => `1px solid ${theme.palette.divider}` }}
				>
					{expanded ? lang.show_less : lang.view_all}
					<KeyboardArrowDown
						sx={{
							fontSize: "1.125rem",
							transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 200ms ease-in-out",
						}}
					/>
				</CardTextButton>
			) : null}
			{actions ? <div style={{ padding: "0.625rem 0.75rem" }}>{actions}</div> : null}
		</GroupCard>
	);
};

/* --- Location (14.png) --- */

export type LocationDetails = {
	title: string;
	address: string;
	latitude: number;
	longitude: number;
	maps_url: string;
};

const LocationLink = styled("a")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "17.5rem",
	maxWidth: "100%",
	borderRadius: "0.75rem",
	overflow: "hidden",
	border: `1px solid ${theme.palette.divider}`,
	background: theme.palette.background.paper,
	color: theme.palette.text.primary,
	textDecoration: "none",
	cursor: "pointer",
}));

/** A shared place: a still map with the pin, then its name and address; opens Google Maps in a new tab. */
export const LocationCard = ({ details }: { details: LocationDetails }) => (
	<LocationLink
		href={details.maps_url}
		target="_blank"
		rel="noopener noreferrer"
	>
		<ChatMapPreview
			latitude={Number(details.latitude)}
			longitude={Number(details.longitude)}
			height="8rem"
			lazy={true}
		/>
		<div style={{ display: "flex", flexDirection: "column", gap: "0.125rem", padding: "0.625rem 0.75rem" }}>
			<Typography
				variant="body2"
				sx={{ fontWeight: 600, ...OneLine }}
			>
				{details.title}
			</Typography>
			{details.address ? (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{
						display: "-webkit-box",
						WebkitLineClamp: 2,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
					}}
				>
					{details.address}
				</Typography>
			) : null}
		</div>
	</LocationLink>
);

/* --- Contact (17.png) --- */

export type ContactDetails = {
	business_profile_id?: string;
	title: string;
	profile_type: string;
	logo_url: string;
	phone: string;
	email: string;
};

const ContactBox = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "17.5rem",
	maxWidth: "100%",
	borderRadius: "0.75rem",
	overflow: "hidden",
	border: `1px solid ${theme.palette.divider}`,
	background: theme.palette.background.paper,
}));

const ContactLine = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.5rem",
	color: theme.palette.text.secondary,
	minWidth: 0,
}));

const ContactActions = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	borderTop: `1px solid ${theme.palette.divider}`,
}));

const ContactAction = styled("a")(({ theme }) => ({
	flex: 1,
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",
	gap: "0.375rem",
	padding: "0.625rem 0.5rem",
	color: theme.palette.primary.dark,
	fontWeight: 500,
	fontSize: "0.875rem",
	textDecoration: "none",
	"& + &": {
		borderLeft: `1px solid ${theme.palette.divider}`,
	},
}));

/** A business's contact card: logo, name, type, phone and email, with Call and Email underneath. */
export const ContactCard = ({ details, locale }: { details: ContactDetails; locale?: string }) => {
	const lang = cardLang(locale);
	const phoneHref: string = details.phone ? `tel:${details.phone.replace(/\s+/g, "")}` : "";
	return (
		<ContactBox>
			<div style={{ display: "flex", flexDirection: "column", gap: "0.625rem", padding: "0.75rem" }}>
				<div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
					<ChatAvatar
						src={details.logo_url}
						name={details.title}
						size="2.75rem"
					/>
					<div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
						<Typography
							variant="body2"
							sx={{ fontWeight: 600, ...OneLine }}
						>
							{details.title}
						</Typography>
						{details.profile_type ? (
							<Typography
								variant="caption"
								color="text.secondary"
							>
								{details.profile_type}
							</Typography>
						) : null}
					</div>
				</div>
				{details.phone ? (
					<ContactLine>
						<PhoneOutlined sx={{ fontSize: "1rem" }} />
						<Typography
							variant="body2"
							sx={{ fontSize: "0.8125rem", ...OneLine }}
						>
							{details.phone}
						</Typography>
					</ContactLine>
				) : null}
				{details.email ? (
					<ContactLine>
						<MailOutline sx={{ fontSize: "1rem" }} />
						<Typography
							variant="body2"
							sx={{ fontSize: "0.8125rem", ...OneLine }}
						>
							{details.email}
						</Typography>
					</ContactLine>
				) : null}
			</div>
			{phoneHref || details.email ? (
				<ContactActions>
					{phoneHref ? (
						<ContactAction href={phoneHref}>
							<PhoneOutlined sx={{ fontSize: "1.125rem" }} />
							{lang.call}
						</ContactAction>
					) : null}
					{details.email ? (
						<ContactAction href={`mailto:${details.email}`}>
							<MailOutline sx={{ fontSize: "1.125rem" }} />
							{lang.email}
						</ContactAction>
					) : null}
				</ContactActions>
			) : null}
		</ContactBox>
	);
};

/* --- Requirement response (20.png) --- */

export type RequirementDetails = {
	requirement_id: string;
	title: string;
	transaction_type: string;
	property_category: string;
	property_type: Array<string>;
	viewer_is_poster: boolean;
};

/* The requirement the response answers: a quiet strip with an accent edge. */
const RequirementStrip = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "0.125rem",
	width: "100%",
	padding: "0.5rem 0.75rem",
	borderRadius: "0.5rem",
	borderLeft: `3px solid ${theme.palette.primary.dark}`,
	background: theme.palette.mode === "dark" ? theme.palette.background.highlight : CARD_HEADER_BG,
}));

const ResponseRows = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	borderRadius: "0.75rem",
	overflow: "hidden",
	border: `1px solid ${theme.palette.divider}`,
	background: theme.palette.background.paper,
}));

/* How many listings a response shows before "+N More attachments". */
const RESPONSE_LISTINGS_COLLAPSED: number = 3;

/**
 * A response to a requirement (the claim composer's "Requirement Response"): the requirement it
 * answers, the header, the words sent with it, and the listings offered, three at first.
 *
 * The strip is not a link: the website shows a requirement's details only inside the dashboard's
 * requirement screens (a modal opened from their own lists), with no page or link a chat could open.
 */
export const RequirementResponseCard = ({
	router,
	details,
	listings,
	body,
	actions,
}: {
	router: NextRouter;
	details: RequirementDetails;
	listings: Array<ListingCard>;
	body: React.ReactNode;
	/* "I'm Interested" / "Not interested" on the other side's response, while unanswered (gap A7). */
	actions?: React.ReactNode;
}) => {
	const lang = cardLang(router.locale);
	const [expanded, setExpanded] = React.useState<boolean>(false);
	const shown: Array<ListingCard> = (listings || []).filter((listing) => !!listing);
	const visible: Array<ListingCard> = expanded ? shown : shown.slice(0, RESPONSE_LISTINGS_COLLAPSED);
	const hidden: number = shown.length - visible.length;
	const title: string =
		details.title ||
		[details.property_category, (details.property_type || []).join(", ")].filter((part) => part && part.trim() !== "").join(" ");
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "0.625rem", width: "18.5rem", maxWidth: "100%" }}>
			<RequirementStrip>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
				>
					<HomeOutlined sx={{ fontSize: "0.875rem" }} />
					{lang.seeking(details.transaction_type)}
				</Typography>
				{title ? (
					<Typography
						variant="body2"
						sx={{ fontSize: "0.8125rem", ...OneLine }}
					>
						{title}
					</Typography>
				) : null}
			</RequirementStrip>
			<Typography
				variant="body2"
				sx={{ display: "flex", alignItems: "center", gap: "0.375rem", fontWeight: 600, color: "primary.dark" }}
			>
				<ReplyOutlined sx={{ fontSize: "1.125rem" }} />
				{lang.requirement_response}
			</Typography>
			{body}
			{visible.length > 0 ? (
				<ResponseRows>
					{visible.map((listing: ListingCard, index: number) => (
						<ListingRow
							key={String(listing.property_id || "") || index}
							router={router}
							listing={listing}
						/>
					))}
				</ResponseRows>
			) : null}
			{hidden > 0 ? (
				<CardTextButton
					type="button"
					onClick={() => setExpanded(true)}
					sx={{ padding: "0rem", alignSelf: "flex-start" }}
				>
					{lang.more_attachments(hidden)}
					<KeyboardArrowDown sx={{ fontSize: "1.125rem" }} />
				</CardTextButton>
			) : null}
			{actions}
		</div>
	);
};

/**
 * "I'm Interested" / "Not interested" under the other side's listings card (gap A7, the app's RequirementResponse
 * CTAs): "I'm Interested" opens the card's listings to pick from, then "I'm interested in these properties" sends
 * those words with the picked listings; "Not interested" dismisses the card. The caller records the decision
 * (chat/update-message-action), so the buttons don't come back.
 */
export const ListingsInterestActions = ({
	router,
	listings,
	onRespond,
	onIgnore,
}: {
	router: NextRouter;
	listings: Array<ListingCard>;
	/* Sends the reply with the picked listing ids; answers whether it went out. */
	onRespond: (propertyIds: Array<string>) => Promise<boolean>;
	/* Answers whether the dismissal was recorded. */
	onIgnore: () => Promise<boolean>;
}) => {
	const tr = (en: string, ar: string, kn: string): string =>
		router.locale === "ar-AE" ? ar : router.locale === "kn-IN" ? kn : en;
	const [picking, setPicking] = React.useState<boolean>(false);
	const [selected, setSelected] = React.useState<Array<string>>([]);
	const [busy, setBusy] = React.useState<boolean>(false);
	const [failed, setFailed] = React.useState<boolean>(false);
	const options: Array<ListingCard> = (listings || []).filter((listing) => listing && listing.property_id);

	const run = async (work: () => Promise<boolean>) => {
		setBusy(true);
		setFailed(false);
		const ok = await work().catch(() => false);
		setBusy(false);
		if (!ok) setFailed(true);
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", width: "100%" }}>
			{picking ? (
				<>
					{options.map((listing) => {
						const id: string = String(listing.property_id);
						return (
							<label
								key={id}
								style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: "pointer" }}
							>
								<Checkbox
									size="small"
									checked={selected.includes(id)}
									disabled={busy}
									onChange={() =>
										setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
									}
								/>
								<Typography
									variant="body2"
									sx={{ ...OneLine }}
								>
									{listing.title || tr("Listing", "عقار", "ಪಟ್ಟಿ")}
								</Typography>
							</label>
						);
					})}
					<BeegruButton
						variant="contained"
						flavor="primary"
						size="small"
						fullWidth
						disabled={busy || selected.length === 0}
						onClick={() => run(() => onRespond(selected))}
					>
						{tr("I'm interested in these properties", "أنا مهتم بهذه العقارات", "ನನಗೆ ಈ ಆಸ್ತಿಗಳಲ್ಲಿ ಆಸಕ್ತಿ ಇದೆ")}
					</BeegruButton>
				</>
			) : (
				<div style={{ display: "flex", gap: "0.5rem" }}>
					<BeegruButton
						variant="contained"
						flavor="primary"
						size="small"
						fullWidth
						disabled={busy}
						onClick={() => setPicking(true)}
					>
						{tr("I'm Interested", "أنا مهتم", "ನನಗೆ ಆಸಕ್ತಿ ಇದೆ")}
					</BeegruButton>
					<BeegruButton
						variant="outlined"
						flavor="primary"
						size="small"
						fullWidth
						disabled={busy}
						onClick={() => run(onIgnore)}
					>
						{tr("Not interested", "غير مهتم", "ಆಸಕ್ತಿ ಇಲ್ಲ")}
					</BeegruButton>
				</div>
			)}
			{failed ? (
				<Typography sx={{ fontSize: "0.75rem", color: "#D32F2F" }}>
					{tr("That didn't go through. Please try again.", "لم يتم ذلك. يرجى المحاولة مرة أخرى.", "ಅದು ಆಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.")}
				</Typography>
			) : null}
		</div>
	);
};
